import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, LayoutDashboard, Users, FileText, Settings, Home, MessageSquareQuote } from 'lucide-react';
import { SidebarProvider } from '@/components/ui/sidebar';
import { AdminSidebar } from '@/components/admin/users/AdminSidebar';
import { useAdminStatus } from '@/hooks/useAdminStatus';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout = ({ children }: AdminLayoutProps) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { isSuperAdmin, isLoading: isAdminLoading } = useAdminStatus();
  const navigate = useNavigate();
  const [currentPath, setCurrentPath] = useState('');
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const [showTimeoutError, setShowTimeoutError] = useState(false);

  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);

  // Set a timeout for the loading state
  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      if (isLoading || isAdminLoading) {
        console.log("Admin layout loading timeout reached");
        setShowTimeoutError(true);
      }
    }, 10000); // 10 seconds timeout
    
    return () => clearTimeout(loadingTimeout);
  }, [isLoading, isAdminLoading]);

  useEffect(() => {
    if (!isLoading && !isAdminLoading) {
      setAuthCheckComplete(true);

      // Only allow access for super admin users
      if (!isAuthenticated || !user || !isSuperAdmin) {
        console.log('Not super admin, redirecting to main site');
        navigate('/');
        return;
      }
      // Super Admin verified, access to admin panel granted
      console.log('Super Admin verified, access to admin panel granted.');
    }
  }, [isLoading, isAuthenticated, user, navigate, isSuperAdmin, isAdminLoading]);

  if (showTimeoutError) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="bg-background p-6 rounded-lg shadow-lg max-w-md text-center">
          <h2 className="text-xl font-bold mb-4 text-destructive">Loading Timed Out</h2>
          <p className="mb-4 text-muted-foreground">
            The admin portal is taking longer than expected to load. This could be due to authentication issues.
          </p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded"
            >
              Retry
            </button>
            <button
              onClick={() => navigate('/login')}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90 px-4 py-2 rounded"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-muted-foreground text-sm">Loading admin portal...</p>
        </div>
      </div>
    );
  }

  // Show nothing until we know exactly
  if (!authCheckComplete || !isAuthenticated || !user || !isSuperAdmin) {
    return null;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-muted/20 flex flex-col sm:flex-row w-full">
        {/* Admin Sidebar - fixed at bottom on mobile, normal sidebar on desktop */}
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t bg-card sm:relative sm:top-0 sm:h-screen sm:w-64 sm:shrink-0 sm:border-r sm:border-t-0">
          <div className="flex h-full flex-col">
            {/* Header - hidden on mobile */}
            <div className="hidden border-b p-4 sm:block">
              <h2 className="text-xl font-semibold text-primary">Admin Portal</h2>
              <p className="text-sm text-muted-foreground">Manage PEO App resources</p>
            </div>
            
            {/* Navigation - using AdminSidebar component */}
            <div className="flex flex-col flex-1 overflow-y-auto">
              <AdminSidebar />
            </div>
            
            {/* Footer - hidden on mobile */}
            <div className="mt-auto hidden border-t p-4 sm:block">
              <div className="text-xs text-muted-foreground">
                <p>© {new Date().getFullYear()} The PEO App</p>
                <p>Admin Console v1.0</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 pb-16 sm:pb-0">
          <div className="bg-background">
            <div className="container mx-auto px-4 py-6">
              {children}
            </div>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};
